import {Component, forwardRef, Input} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from "@angular/forms";
import {SubscribedComponent} from "../../subscribed.component";
import {DomSanitizer} from "@angular/platform-browser";
import {Globals} from "../../shared/Globals";

export interface SliderValue{
  gte: number|null,
  lte: number|null
}

@Component({
    selector: 'bst-dual-slider',
    templateUrl: './dual-slider.component.html',
    styleUrls: ['./dual-slider.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DualSliderComponent),
            multi: true
        }
    ],
    standalone: false
})
export class DualSliderComponent extends SubscribedComponent implements ControlValueAccessor{

  @Input() currency: boolean = false;
  @Input() range: SliderValue = {
    gte: 0,
    lte: 100
  };

  Globals = Globals;
  public value: SliderValue = null;
  public lowerfield: FormControl = new FormControl(null, []);
  public upperfield: FormControl = new FormControl(null, []);
  public upper: FormControl = new FormControl(null, []);
  public lower: FormControl = new FormControl(null, []);
  public step;

  onChange = (value: SliderValue) => {};
  onTouched = () => {};

  constructor(private sanitizer: DomSanitizer) {
    super();
  }

  get left(){
    if(this.range.lte) {
      return this.sanitizer.bypassSecurityTrustStyle('calc('+(Math.max(this.lower.value, 0) / this.range.lte * 100)+'% + 5px)');
    }
    return 0;
  }

  get width(){
    const size = this.range.lte - this.range.gte;
    if(size){
      const wi = Math.max(Math.min(this.upper.value, this.range.lte) - Math.max(this.lower.value, 0), 0);
      return this.sanitizer.bypassSecurityTrustStyle('calc('+(wi / size * 100)+'% - 10px)');
    }
    return 0;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.step = this.range.lte / 100;

    this.subs.push(this.upper.valueChanges.subscribe(upperVal => {
      const lowerVal = this.lower.value || 0;
      if (upperVal < lowerVal) {
        this.lower.setValue(Math.max(upperVal, this.range.gte));
      }
      if (this.upper.value != this.upperfield.value && this.upper.value <= this.range.lte) {
        this.upperfield.setValue(this.upper.value);
      }
    }));

    this.subs.push(this.lower.valueChanges.subscribe(lowerVal => {
      const upperVal = this.upper.value || 0;
      if (lowerVal > upperVal) {
        this.upper.setValue(Math.min(lowerVal, this.range.lte));
      }
      if (this.lower.value != this.lowerfield.value && this.lower.value >= this.range.gte) {
        this.lowerfield.setValue(this.lower.value);
      }
    }));

    this.subs.push(this.upperfield.valueChanges.subscribe(upperVal => {
      if(upperVal === null || !upperVal.toString().length){
        this.upper.setValue(this.range.lte + 1);
        this.value = {
          gte: this.value && this.value.gte !== null ? this.value.gte : null,
          lte: null
        };
        if(this.value.gte === null && this.value.lte === null){
          this.value = null
        }
      }
      else {
        this.upper.setValue(upperVal);
        this.value = {
          gte: this.value && this.value.gte !== null ? this.value.gte : null,
          lte: upperVal,
        };
      }
      this.onChange(this.value);
    }));

    this.subs.push(this.lowerfield.valueChanges.subscribe(lowerVal => {
      if(lowerVal === null || !lowerVal.toString().length){
        this.lower.setValue(this.range.gte - 1);
        this.value = {
          lte: this.value && this.value.lte !== null ? this.value.lte : null,
          gte: null
        };
        if(this.value.gte === null && this.value.lte === null){
          this.value = null
        }
      }
      else {
        this.lower.setValue(lowerVal);
        this.value = {
          lte: this.value && this.value.lte !== null ? this.value.lte : null,
          gte: lowerVal
        };
      }
      this.onChange(this.value);
    }));
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if(isDisabled){
      this.upper.disable();
      this.lower.disable();
      this.upperfield.disable();
      this.lowerfield.disable();
    }
    else{
      this.upper.enable();
      this.lower.enable();
      this.upperfield.enable();
      this.lowerfield.enable();
    }
  }

  writeValue(value: SliderValue): void {
    this.lowerfield.setValue(value && value.gte !== null ? value.gte : null);
    this.upperfield.setValue(value && value.lte !== null ? value.lte : null);
    this.onChange(this.value);
  }
}
